import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Download, FileSpreadsheet, Search, Loader2, Inbox, Trash2, Mail, Phone, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DeleteLeadDialog } from "@/components/DeleteLeadDialog";
import { HistoryLogin } from "@/components/HistoryLogin";
import { TricolorBar } from "@/components/FormHeader";
import { TIPI_SCHEDA, REPARTI } from "@/lib/clienteConstants";
import { API, TOKEN_KEY } from "@/lib/api";

const ClienteCard = ({ c, onOpen, onDelete, deleting }) => (
  <div
    role="button"
    tabIndex={0}
    data-testid={`cliente-card-${c.id}`}
    onClick={onOpen}
    onKeyDown={(e) => e.key === "Enter" && onOpen()}
    className="text-left bg-white rounded-2xl border border-gray-200 border-l-4 border-l-buscema-gold p-4 shadow-sm transition-[box-shadow,border-color,transform] hover:shadow-md hover:border-l-buscema-green active:scale-[0.99] cursor-pointer"
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <h3 className="font-heading font-semibold text-buscema-green truncate">{c.ragione_sociale || c.insegna || "—"}</h3>
        <p className="text-sm text-gray-600 truncate">{c.referenti?.[0]?.nome} {c.referenti?.length > 1 && `+${c.referenti.length - 1}`}</p>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {c.referenti?.map((r) => (
            <span key={r.reparto} className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-buscema-green/5 text-buscema-green">{r.reparto}</span>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <span className={`text-[11px] font-semibold px-2 py-1 rounded-lg ${c.tipo_scheda === "Nuovo contatto" ? "bg-buscema-green text-white" : "bg-buscema-gold/15 text-buscema-gold"}`}>
          {c.tipo_scheda === "Nuovo contatto" ? "Nuovo" : "Aggiornamento"}
        </span>
        <DeleteLeadDialog name={c.ragione_sociale || c.referenti?.[0]?.nome} onConfirm={onDelete} deleting={deleting} testIdSuffix={`-${c.id}`}>
          <button
            type="button"
            data-testid={`cliente-card-delete-${c.id}`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            aria-label="Elimina scheda"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </DeleteLeadDialog>
      </div>
    </div>
    <div className="mt-3 text-sm text-gray-500 space-y-0.5">
      {c.referenti?.[0]?.email && <p className="truncate flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 shrink-0" />{c.referenti[0].email}</p>}
      {(c.referenti?.[0]?.cellulare || c.referenti?.[0]?.telefono) && <p className="truncate flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 shrink-0" />{c.referenti[0].cellulare || c.referenti[0].telefono}</p>}
      {c.sede_citta && <p className="truncate flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 shrink-0" />{c.sede_citta} {c.sede_provincia && `(${c.sede_provincia})`}</p>}
      <p className="text-xs text-gray-400 pt-1">{c.data_inserimento ? new Date(c.data_inserimento).toLocaleDateString("it-IT") : ""} {c.partita_iva && `· P.IVA ${c.partita_iva}`}</p>
    </div>
  </div>
);

const FilterSelect = ({ value, onChange, testid, allLabel, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="min-h-[52px] rounded-xl"><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{allLabel}</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function ClientiHistory() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [fTipo, setFTipo] = useState("all");
  const [fReparto, setFReparto] = useState("all");
  const [deletingId, setDeletingId] = useState(null);

  const params = useCallback(() => {
    const p = {};
    if (q) p.q = q;
    if (fTipo !== "all") p.tipo_scheda = fTipo;
    if (fReparto !== "all") p.reparto = fReparto;
    return p;
  }, [q, fTipo, fReparto]);

  const handleAuthError = (err, msg) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      toast.error("Sessione scaduta, effettua di nuovo l'accesso");
    } else {
      toast.error(`${msg}${err.response?.status ? ` (${err.response.status})` : ""}`);
    }
  };

  const fetchItems = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clienti`, { headers: { Authorization: `Bearer ${token}` }, params: params() });
      setItems(res.data);
    } catch (err) {
      handleAuthError(err, "Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, [token, params]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleExport = async (format) => {
    try {
      const res = await axios.get(`${API}/clienti/export`, { headers: { Authorization: `Bearer ${token}` }, params: { ...params(), format }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `buscema_clienti.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format === "xlsx" ? "Excel" : "CSV"} avviato`);
    } catch (err) {
      handleAuthError(err, "Errore durante l'export");
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API}/clienti/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Scheda eliminata");
    } catch (err) {
      handleAuthError(err, "Errore durante l'eliminazione");
    } finally {
      setDeletingId(null);
    }
  };

  if (!token) return <HistoryLogin title="Elenco Anagrafiche Clienti" backPath="/anagrafica" onToken={setToken} />;

  return (
    <div className="min-h-screen bg-gray-50 font-body" data-testid="clienti-history-page">
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn" className="text-buscema-green">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold text-buscema-green">Elenco Anagrafiche Clienti</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="export-xlsx-btn" onClick={() => handleExport("xlsx")} className="bg-buscema-green hover:bg-buscema-greenDark text-white gap-2 h-11 rounded-xl">
              <FileSpreadsheet className="w-4 h-4" /> <span className="hidden sm:inline">Esporta Excel</span>
            </Button>
            <Button data-testid="export-csv-btn" onClick={() => handleExport("csv")} variant="outline" className="border-2 border-buscema-gold text-buscema-gold hover:bg-buscema-gold hover:text-white gap-2 h-11 rounded-xl bg-white">
              <Download className="w-4 h-4" /> <span className="hidden sm:inline">CSV</span>
            </Button>
          </div>
        </div>
        <TricolorBar />
      </header>
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input data-testid="filter-q-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca ragione sociale, insegna, referente" className="min-h-[52px] rounded-xl pl-10" />
          </div>
          <FilterSelect value={fTipo} onChange={setFTipo} testid="filter-tipo-select" allLabel="Tutti i tipi" options={TIPI_SCHEDA} />
          <FilterSelect value={fReparto} onChange={setFReparto} testid="filter-reparto-select" allLabel="Tutti i reparti" options={REPARTI} />
        </div>
        <p className="text-sm text-gray-500" data-testid="clienti-count">{items.length} anagrafiche trovate</p>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-buscema-green" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-gray-400" data-testid="empty-state">
            <Inbox className="w-12 h-12 mb-3" />
            <p>Nessuna anagrafica registrata</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((c) => (
              <ClienteCard key={c.id} c={c} onOpen={() => navigate(`/anagrafica/elenco/${c.id}`)} onDelete={() => handleDelete(c.id)} deleting={deletingId === c.id} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
